/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Linking, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { showSuccess, showError } from '../../../shared/ui/appToast';
import { storefrontPublicUrl } from '../../domain/storeProfileTypes';

type Props = {
  slug: string;
  portalBaseUrl: string;
};

export function StorefrontCopyLinkCard({ slug, portalBaseUrl }: Props) {
  const [copied, setCopied] = useState(false);
  const storeUrl = storefrontPublicUrl(portalBaseUrl, slug);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const handleCopy = useCallback(async () => {
    if (!slug) return;
    try {
      await Clipboard.setStringAsync(storeUrl);
      setCopied(true);
      showSuccess('Link copiado', 'Pegalo en WhatsApp, Instagram o donde quieras.');
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      showError('No se pudo copiar', 'Seleccioná el link y copialo a mano.');
    }
  }, [slug, storeUrl]);

  if (!slug) return null;

  return (
    <PaperSheet padded testID="storefront-copy-link">
      <View style={local.row}>
        <View style={{ flex: 1, minWidth: 200 }}>
          <Text style={styles.captionStrong}>Link de tu tienda</Text>
          <Text style={local.url} selectable numberOfLines={1}>
            {storeUrl}
          </Text>
        </View>
        <View style={local.btnRow}>
          <TouchableOpacity
            style={local.copyBtn}
            onPress={() => void handleCopy()}
            accessibilityRole="button"
            accessibilityLabel="Copiar link"
            testID="config.storefront.copy-link"
          >
            <Text style={local.copyBtnText}>{copied ? 'Copiado' : 'Copiar link'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={local.viewBtn}
            onPress={() => void Linking.openURL(storeUrl)}
            accessibilityRole="button"
            accessibilityLabel="Ver mi tienda"
            testID="config.storefront.view-store"
          >
            <Text style={local.viewBtnText}>Ver mi tienda</Text>
          </TouchableOpacity>
        </View>
      </View>
    </PaperSheet>
  );
}

const local = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  btnRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  url: {
    fontSize: 13,
    fontFamily: ticketFonts.mono ?? ticketFonts.sans,
    color: ticketColors.ink2,
    marginTop: 2,
  },
  copyBtn: {
    alignSelf: 'flex-start',
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    minHeight: 40,
    justifyContent: 'center',
  },
  copyBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  viewBtn: {
    alignSelf: 'flex-start',
    backgroundColor: 'transparent',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 10,
    paddingHorizontal: 14,
    minHeight: 40,
    justifyContent: 'center',
  },
  viewBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
});
